<template>
  <b-card title="Импорт расписания">
    <b-overlay :show="isLoading" rounded="sm">

      <!-- Выбор недели -->
      <b-form-group label="Игровая неделя" label-for="week-select" class="mb-2">
        <v-select
            id="week-select"
            v-model="selectedWeek"
            :options="weeks"
            label="name"
            :reduce="w => w"
            placeholder="Выберите неделю..."
            @input="onWeekChange"
        />
      </b-form-group>

      <!-- Текстовое поле для вставки расписания -->
      <b-form-group label="Расписание" label-for="schedule-text" class="mb-2">
        <b-form-textarea
            id="schedule-text"
            v-model="scheduleText"
            rows="12"
            placeholder="Вставьте расписание в формате:
20.02.2026 12:00 - Алина Манохина
20.02.2026 18:30 - Серега Ворона"
        />
      </b-form-group>

      <!-- Кнопки -->
      <div class="d-flex mb-2" style="gap: 10px;">
        <b-button
            variant="primary"
            :disabled="!scheduleText || !selectedWeek"
            @click="parseSchedule"
        >
          <feather-icon icon="UploadIcon" class="mr-50" />
          Загрузить
        </b-button>
      </div>

      <!-- Легенда тем -->
      <div v-if="parsedRows.length > 0 && topics.length > 0" class="mb-2">
        <h5>Легенда тем</h5>
        <div class="d-flex flex-wrap" style="gap: 10px;">
          <b-badge
              v-for="topic in topics"
              :key="topic.id"
              :style="{ backgroundColor: topic._color, color: '#fff', fontSize: '14px', padding: '6px 12px' }"
          >
            {{ topic.emoji }} {{ topic.name }}
          </b-badge>
        </div>
      </div>

      <!-- Таблица -->
      <div v-if="parsedRows.length > 0" class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th style="min-width: 180px;">Дата</th>
              <th
                  v-for="topic in topics"
                  :key="'th-' + topic.id"
                  class="text-center"
                  style="min-width: 70px;"
              >
                {{ topic.emoji }} {{ topic.name }}
              </th>
              <th>Ведущий</th>
              <th style="width: 50px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr
                v-for="(row, index) in parsedRows"
                :key="index"
                :style="getRowStyle(row, index)"
            >
              <!-- Дата -->
              <td>
                <div class="d-flex align-items-center">
                  <span class="mr-1 text-nowrap">{{ row.dateLabel }}</span>
                  <b-form-select
                      v-model="row.hours"
                      :options="hourOptions"
                      style="width: 70px;"
                      size="sm"
                      class="mr-50"
                  />
                  <span class="mr-50">:</span>
                  <b-form-select
                      v-model="row.minutes"
                      :options="minuteOptions"
                      style="width: 70px;"
                      size="sm"
                  />
                </div>
              </td>

              <!-- Кнопки тем (радио) -->
              <td
                  v-for="topic in topics"
                  :key="'td-' + topic.id + '-' + index"
                  class="text-center align-middle"
              >
                <b-button
                    :variant="row.selectedTopicId === topic.id ? 'primary' : 'outline-secondary'"
                    size="sm"
                    pill
                    @click="toggleTopic(row, topic.id)"
                >
                  {{ topic.emoji }}
                </b-button>
              </td>

              <!-- Ведущий -->
              <td class="align-middle">
                {{ row.host }}
              </td>

              <!-- Удалить -->
              <td class="text-center align-middle">
                <b-button variant="flat-danger" size="sm" @click="removeRow(index)">
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Валидация -->
      <b-alert v-if="validationError" show variant="danger" class="mt-1">
        {{ validationError }}
      </b-alert>

      <!-- Кнопка Отправить -->
      <b-button
          v-if="parsedRows.length > 0"
          variant="success"
          class="mt-1"
          :disabled="isSending"
          @click="submitSchedule"
      >
        <span v-if="isSending">
          <b-spinner small />
          Отправка...
        </span>
        <span v-else>
          <feather-icon icon="SendIcon" class="mr-50" />
          Отправить
        </span>
      </b-button>

    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BFormTextarea,
  BFormInput,
  BFormSelect,
  BButton,
  BOverlay,
  BBadge,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted, ref, computed } from '@vue/composition-api'
import store from '@/store'
import gameStoreModule from '@/views/game/gameStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const THEME_COLORS = [
  '#7367f0', // фиолетовый
  '#28c76f', // зелёный
  '#ff9f43', // оранжевый
  '#00cfe8', // голубой
  '#ea5455', // красный
  '#a855f7', // пурпурный
  '#f59e0b', // жёлтый
  '#06b6d4', // бирюзовый
]

export default {
  components: {
    BCard,
    BFormGroup,
    BFormTextarea,
    BFormInput,
    BFormSelect,
    BButton,
    BOverlay,
    BBadge,
    BAlert,
    BSpinner,
    vSelect,
  },
  setup() {
    const STORE_MODULE_NAME = 'app-games'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()

    const hourOptions = Array.from({ length: 24 }, (_, i) => ({
      value: String(i).padStart(2, '0'),
      text: String(i).padStart(2, '0'),
    }))
    const minuteOptions = Array.from({ length: 12 }, (_, i) => ({
      value: String(i * 5).padStart(2, '0'),
      text: String(i * 5).padStart(2, '0'),
    }))

    const isLoading = ref(false)
    const isSending = ref(false)
    const weeks = ref([])
    const selectedWeek = ref(null)
    const scheduleText = ref('')
    const parsedRows = ref([])
    const validationError = ref('')

    const topics = computed(() => {
      if (!selectedWeek.value || !selectedWeek.value.topics) return []
      return selectedWeek.value.topics.map((t, i) => ({
        ...t,
        _color: THEME_COLORS[i % THEME_COLORS.length],
      }))
    })

    // Загрузка списка недель
    isLoading.value = true
    store.dispatch('app-games/fetchGameWeeks')
        .then(response => {
          weeks.value = response.data.data
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка загрузки недель',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoading.value = false
        })

    const onWeekChange = () => {
      parsedRows.value = []
      validationError.value = ''
    }

    // Парсинг текста расписания
    const parseSchedule = () => {
      validationError.value = ''
      const lines = scheduleText.value
          .split('\n')
          .map(l => l.trim())
          .filter(l => l.length > 0)

      const rows = []
      for (const line of lines) {
        // Формат: DD.MM.YYYY HH:MM - Имя Фамилия
        const match = line.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{1,2}):(\d{2})\s*-\s*(.+)$/)
        if (!match) {
          validationError.value = `Не удалось распарсить строку: "${line}"`
          parsedRows.value = []
          return
        }

        const [, day, month, year, hours, minutes, host] = match
        const roundedMinutes = Math.round(parseInt(minutes, 10) / 5) * 5
        rows.push({
          day,
          month,
          year,
          hours: String(parseInt(hours, 10)).padStart(2, '0'),
          minutes: String(roundedMinutes % 60).padStart(2, '0'),
          host: host.trim(),
          dateLabel: `${day}.${month}.${year}`,
          selectedTopicId: null,
        })
      }

      parsedRows.value = rows
    }

    // Переключение темы (радио-поведение)
    const toggleTopic = (row, topicId) => {
      if (row.selectedTopicId === topicId) {
        row.selectedTopicId = null
      } else {
        row.selectedTopicId = topicId
      }
    }

    // Стиль строки по выбранной теме + разделение дней
    const getRowStyle = (row, index) => {
      const style = {}

      // Первая строка нового дня — заметный разделитель сверху
      const isNewDay = index > 0 && parsedRows.value[index - 1].dateLabel !== row.dateLabel
      if (isNewDay) {
        style.borderTop = '3px solid #d8d6de'
      }

      if (row.selectedTopicId) {
        const topic = topics.value.find(t => t.id === row.selectedTopicId)
        if (topic) {
          style.backgroundColor = topic._color + '20'
          style.borderLeft = `4px solid ${topic._color}`
        }
      }

      return style
    }

    // Удаление строки
    const removeRow = (index) => {
      parsedRows.value.splice(index, 1)
    }

    // Валидация и отправка
    const submitSchedule = () => {
      validationError.value = ''

      const missingRows = parsedRows.value.filter(r => !r.selectedTopicId)
      if (missingRows.length > 0) {
        validationError.value = `Для ${missingRows.length} строк(и) не выбрана тема. Выберите тему для каждой строки.`
        return
      }

      const payload = {
        week_id: selectedWeek.value.id,
        items: parsedRows.value.map(row => ({
          date: `${row.year}-${row.month}-${row.day}`,
          time: `${String(row.hours).padStart(2, '0')}:${String(row.minutes).padStart(2, '0')}`,
          topic_id: row.selectedTopicId,
          host: row.host,
        })),
      }

      isSending.value = true
      store.dispatch('app-games/submitSchedule', payload)
          .then(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Расписание успешно отправлено',
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
            parsedRows.value = []
            scheduleText.value = ''
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Ошибка при отправке расписания',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
          .finally(() => {
            isSending.value = false
          })
    }

    return {
      isLoading,
      isSending,
      weeks,
      selectedWeek,
      scheduleText,
      parsedRows,
      topics,
      validationError,
      hourOptions,
      minuteOptions,
      onWeekChange,
      parseSchedule,
      toggleTopic,
      getRowStyle,
      removeRow,
      submitSchedule,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
